import {
  Button,
  Dialog,
  DialogContent,
  FormControl,
  MenuItem,
  InputLabel,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useMemo, useState, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useGlobalCore } from '../../../../core/modules/global';
import { CREATE_RECEPTION_ORDER_DIALOG } from '../../../../core/constants/dialog';
import Spinner from '../../Spinner';
import { useCompanyCore } from '../../../../core/modules/company';
import { useReceptionCore } from '../../../../core/modules/reception';
import { useBusinessCore } from '../../../../core/modules/business';
import moment from 'moment';
import { DOCUMENT_TYPES } from '../../../../core/constants/documentTypes';
import { useMasterCore } from '../../../../core/modules/master';
import { isEmpty } from 'lodash';

const CreateDialog = () => {
  const { dialogs, closeDialog } = useGlobalCore();
  const { postReceptionOrder, getReceptionOrders } = useReceptionCore();
  const { companyId } = useCompanyCore();
  const { acquisitionTypes } = useMasterCore();
  const { businesses } = useBusinessCore();

  const [loading, setLoading] = useState(false);

  // Esquema de validación base
  const baseSchema = yup.object().shape({
    reception_date: yup.string().required('Campo requerido'),
    description: yup.string().optional(),
    cost: yup.number().min(0, 'Mínimo valor permitido es 0').typeError('Formato incorrecto'),
    document_number: yup.string().optional(),
    document_type_id: yup.string().optional(),
    business_id: yup.number().required('Campo requerido'),
    area_id: yup.number().required('Campo requerido'),
    acquisition_type_id: yup.string().required('Campo requerido'),
    commission: yup.number().min(0, 'Mínimo valor permitido es 0').max(100, 'Máximo valor permitido es 100').typeError('Formato incorrecto'),
    notes: yup.string().optional(),
  });

  const { control, formState, handleSubmit, reset, setValue, watch } = useForm({
    mode: 'onChange',
    defaultValues: {
      reception_date: moment().format('yyyy-MM-DD'),
    },
    resolver: yupResolver(baseSchema),
  });

  const { isValid, errors } = formState;

  // Observar cambios en el proveedor, área y tipo de adquisición seleccionados
  const selectedBusinessId = watch('business_id');
  const selectedAreaId = watch('area_id');
  const selectedAcquisitionTypeId = watch('acquisition_type_id');

  // Obtener información del tipo de adquisición seleccionado
  const selectedAcquisitionType = useMemo(() => {
    if (!selectedAcquisitionTypeId) return null;
    return (
      acquisitionTypes.find((type) => type.id === Number(selectedAcquisitionTypeId)) || null
    );
  }, [selectedAcquisitionTypeId, acquisitionTypes]);

  // Función helper para encontrar el área principal de una empresa
  const findBusinessMainArea = useCallback((business: any) => {
    if (!business?.areas || business.areas.length === 0) return null;
    // El área principal es la que tiene el mismo acrónimo que la empresa
    return (
      business.areas.find((area: any) => area.acronym === business.acronym) ||
      business.areas[0]
    );
  }, []);

  // Obtener áreas del proveedor seleccionado
  const availableAreas = useMemo(() => {
    if (!selectedBusinessId) return [];
    const selectedBusiness = businesses.find(
      (b) => b.id === Number(selectedBusinessId)
    );
    return selectedBusiness?.areas || [];
  }, [selectedBusinessId, businesses]);

  // Obtener información del área seleccionada
  const selectedProviderArea = useMemo(() => {
    if (!selectedAreaId || availableAreas.length === 0) return null;
    return (
      availableAreas.find((area) => area.id === Number(selectedAreaId)) || null
    );
  }, [selectedAreaId, availableAreas]);

  // Efecto para limpiar área y actualizar con área principal cuando cambie el proveedor
  useEffect(() => {
    if (selectedBusinessId) {
      const selectedBusiness = businesses.find(
        (b) => b.id === Number(selectedBusinessId)
      );
      if (selectedBusiness) {
        const mainArea = findBusinessMainArea(selectedBusiness);
        if (mainArea) {
          // Auto-seleccionar el área principal
          setValue('area_id', mainArea.id);
          setValue('commission', mainArea.commission);
        }
      }
    }
  }, [selectedBusinessId, businesses, findBusinessMainArea, setValue]);

  // Efecto para actualizar la comisión cuando cambie el área
  useEffect(() => {
    if (selectedAreaId && availableAreas.length > 0) {
      const selectedArea = availableAreas.find(
        (area) => area.id === Number(selectedAreaId)
      );
      if (selectedArea?.commission) {
        setValue('commission', selectedArea.commission);
      }
    }
  }, [selectedAreaId, availableAreas, setValue]);

  // Efecto para actualizar costo y comisión cuando cambie el tipo de adquisición
  useEffect(() => {
    if (selectedAcquisitionType) {
      if (!selectedAcquisitionType.requires_cost) {
        setValue('cost', 0);
      }
      if (!selectedAcquisitionType.requires_commission) {
        setValue('commission', 0);
      }
    }
  }, [selectedAcquisitionType, setValue]);

  const resetForm = useCallback(() => {
    reset({
      reception_date: moment().format('yyyy-MM-DD'),
      description: '',
      cost: undefined,
      document_number: '',
      document_type_id: '',
      acquisition_type_id: '',
      commission: undefined,
      notes: '',
      business_id: undefined,
      area_id: undefined,
    });
    closeDialog(CREATE_RECEPTION_ORDER_DIALOG);
  }, [closeDialog, reset]);

  const onSubmit = useCallback(
    (values: any) => {
      if (companyId) {
        setLoading(true);
        postReceptionOrder(
          companyId,
          {
            ...values,
            document_type_id: values.document_type_id ? +values.document_type_id : undefined,
            acquisition_type_id: +values.acquisition_type_id,
            business_id: +values.business_id,
            business_area_id: +values.area_id,
            reception_date: moment(values.reception_date).unix(),
          },
          () => {
            resetForm();
            getReceptionOrders(companyId);
            setLoading(false);
          },
          () => {
            setLoading(false);
          }
        );
      }
    },
    [companyId, postReceptionOrder, resetForm, getReceptionOrders]
  );

  // Limpiar formulario cuando se cierre el diálogo
  useEffect(() => {
    const isDialogOpen = Array.isArray(dialogs) && dialogs.includes(CREATE_RECEPTION_ORDER_DIALOG);
    if (!isDialogOpen) {
      // Si el diálogo se cierra, limpiar los valores
      reset({
        reception_date: moment().format('yyyy-MM-DD'),
        description: '',
        cost: undefined,
        document_number: '',
        document_type_id: '',
        acquisition_type_id: '',
        commission: undefined,
        notes: '',
        business_id: undefined,
        area_id: undefined,
      });
    }
  }, [dialogs, reset]);

  const providers = useMemo(() => {
    return businesses.filter((business) => business.is_provider && business.areas && business.areas.length > 0);
  }, [businesses]);

  return (
    <Dialog
      classes={{
        paper: 'm-8 sm:m-24 min-w-[350px]',
      }}
      onClose={resetForm}
      open={Array.isArray(dialogs) && Array.isArray(dialogs) && dialogs.includes(CREATE_RECEPTION_ORDER_DIALOG)}
    >
      <DialogContent>
        <div className="flex flex-auto flex-col py-16 px-0 sm:px-16">
          <Typography className="text-2xl font-bold truncate mb-16">
            Generar orden de ingreso
          </Typography>

          <form
            noValidate
            onSubmit={handleSubmit(onSubmit)}
            className="flex flex-col space-y-24"
          >
            <Controller
              name="reception_date"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Fecha de Recepción"
                  type="date"
                  variant="outlined"
                  required
                  fullWidth
                />
              )}
            />

            <Controller
              name="business_id"
              control={control}
              render={({ field }) => (
                <FormControl variant="outlined" fullWidth>
                  <InputLabel>Proveedor</InputLabel>
                  <Select {...field} label="Proveedor">
                    {providers.map((seller) => (
                      <MenuItem key={seller.id} value={seller.id}>
                        {seller.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />

            {selectedBusinessId && availableAreas.length > 0 && (
              <Controller
                name="area_id"
                control={control}
                render={({ field }) => (
                  <FormControl variant="outlined" fullWidth>
                    <InputLabel>Área</InputLabel>
                    <Select
                      {...field}
                      label="Área"
                      disabled={loading}
                      error={!!errors.area_id}
                    >
                      {availableAreas.map((area) => (
                        <MenuItem key={area.id} value={area.id}>
                          {area.acronym} - {area.commission}%
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.area_id && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ mt: 1, ml: 2 }}
                      >
                        {errors.area_id.message}
                      </Typography>
                    )}
                  </FormControl>
                )}
              />
            )}

            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Descripción"
                  variant="outlined"
                  fullWidth
                  inputProps={{
                    style: { textTransform: 'uppercase' }
                  }}
                  onChange={(e) => {
                    field.onChange(e.target.value.toUpperCase());
                  }}
                />
              )}
            />

            <Controller
              name="acquisition_type_id"
              control={control}
              render={({ field }) => (
                <FormControl variant="outlined" fullWidth required>
                  <InputLabel id="type-label">Tipo de adquisición</InputLabel>
                  <Select
                    {...field}
                    labelId="type-label"
                    label="Tipo de adquisición"
                    disabled={loading}
                    required
                  >
                    {acquisitionTypes.map((type) => (
                      <MenuItem key={type.id} value={type.id}>
                        {type.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />

            {selectedAcquisitionType?.requires_cost && (
              <Controller
                name="cost"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Costo"
                    type="number"
                    variant="outlined"
                    fullWidth
                  />
                )}
              />
            )}

            {selectedAcquisitionType?.requires_commission && (
              <Controller
                name="commission"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={
                      selectedProviderArea
                        ? `% de Comisión - Área: ${selectedProviderArea.acronym}`
                        : '% de Comisión'
                    }
                    type="number"
                    variant="outlined"
                    error={!!errors.commission}
                    helperText={
                      errors.commission?.message ||
                      (selectedProviderArea
                        ? `Comisión del área "${selectedProviderArea.acronym}" (${selectedProviderArea.commission}%). Puede modificar este valor.`
                        : selectedBusinessId
                        ? 'Seleccione un área para obtener la comisión automáticamente.'
                        : 'Seleccione un proveedor y área para obtener la comisión automáticamente.')
                    }
                    fullWidth
                    inputProps={{
                      min: 0,
                      max: 100,
                      step: 0.1,
                    }}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Validar que no sea mayor a 100
                      if (value === '' || (parseFloat(value) >= 0 && parseFloat(value) <= 100)) {
                        field.onChange(value);
                      }
                    }}
                  />
                )}
              />
            )}

            <Controller
              name="document_type_id"
              control={control}
              render={({ field }) => (
                <FormControl variant="outlined" fullWidth>
                  <InputLabel id="document_type-label">
                    Tipo de documento (Opcional)
                  </InputLabel>
                  <Select
                    {...field}
                    labelId="document_type-label"
                    id="document_type-label"
                    label="Tipo de documento (Opcional)"
                    disabled={loading}
                  >
                    <MenuItem value="">
                      <em>Ninguno</em>
                    </MenuItem>
                    {DOCUMENT_TYPES.map(({ name, value }) => (
                      <MenuItem key={value} value={value}>
                        {name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />

            <Controller
              name="document_number"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Número de Documento (Opcional)"
                  variant="outlined"
                  fullWidth
                  onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                />
              )}
            />

            <Controller
              name="notes"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  multiline
                  label="Observación"
                  variant="outlined"
                  fullWidth
                />
              )}
            />

            <Button
              variant="contained"
              color="secondary"
              disabled={!isEmpty(errors) || !isValid || loading}
              type="submit"
              size="large"
            >
              <Spinner show={loading} /> Generar
            </Button>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CreateDialog;
